import { Component, DoCheck, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AddOn, AddOnUnitType } from 'anchor-shared/lib/model/entity/addOn';
import { getAddOnsTotal } from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { roundToNearestCent } from 'anchor-shared/lib/service/utils/helper';
import { CustomerAddOn } from 'anchor-shared/lib/model/entity/addOn';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';
import { Observable, Subscription } from 'rxjs';
import { PaymentDAO } from '../../../../core/services/dao/paymentDAO';
import moment from 'moment';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { getPromoAmountFromCharter } from '../../../../core/pure-functions/promo-codes/promo-code-helper';
import { getRoundedFullPaymentAmount } from '../../../../core/pure-functions/charter-pricing-helpers';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentProcessor } from 'anchor-shared/lib/model/enum/payment-processor';
import { NumberFormatterService } from '../../../../core/services/number.formatter.service';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { AddOnDAO } from '../../../../core/services/dao/addOnDAO';
import { getTypeofCharter } from 'anchor-shared/lib/service/utils/charter-helper';
import { CharterStatus } from 'anchor-shared/lib/model/enum/charter-status';

@Component({
  selector: 'customer-add-ons-dialog',
  templateUrl: './customer-add-ons-dialog.component.html',
  styleUrls: ['./customer-add-ons-dialog.component.scss'],
})
export class CustomerAddOnsDialogComponent implements OnInit, DoCheck {
  addOns: CustomerAddOn[];
  charterBasePrice: number;
  requiredAddOnsTotal: number = 0;
  optionalAddOnsTotal: number = 0;
  charterDuration: number = 0;
  charter: Charter;
  createAdjustment: boolean;
  // utility
  loading: boolean = true;
  isMobile: boolean;
  AddOnUnitType = AddOnUnitType;
  subs: Subscription[] = [];

  constructor(
    private paymentDAO: PaymentDAO,
    private addOnDAO: AddOnDAO,
    private snackBar: MatSnackBar,
    private numFormatter: NumberFormatterService,
    public dialogRef: MatDialogRef<CustomerAddOnsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.addOns = this.data.addOns.map((addOn) => ({
      ...addOn,
      quantity: addOn.quantity || 0,
      markupPrice: addOn.markupPercentage
        ? roundToNearestCent(addOn.price * (1 + addOn.markupPercentage))
        : null,
    }));
    this.charterBasePrice = this.data.charterBasePrice;
    this.requiredAddOnsTotal = this.data.requiredAddOnsTotal;
    this.charterDuration = this.data.duration;
    this.charter = this.data.charter;
    this.createAdjustment = this.data.createAdjustment;
    this.isMobile = this.data.isMobile;
    this.loading = this.data.loading;

    this.calculateOptionalAddOnsTotal();
  }

  public submitAddOns = async () => {
    if (this.createAdjustment) {
      await this.manageAdjustmentPaymentFromCharter(this.charter).toPromise();
      const charterStatus = getTypeofCharter(this.charter);
      if (
        CharterStatus.booked === charterStatus ||
        this.charter.paymentReceived
      ) {
        await this.addOnDAO
          .notifyAdminOfAddOnAdjustment(this.charter.id)
          .subscribe();
      }
      this.openSnackbar('Your add ons have successfully been updated.');
    }
    const selectedOptions: AddOn[] = this.addOns.filter((x) => x.quantity > 0);
    this.dialogRef.close(selectedOptions);
  };

  ngOnInit(): void {}

  ngDoCheck() {
    if (this.loading && this.addOns.length !== this.data.addOns.length) {
      this.addOns = this.data.addOns.map((x) => ({ ...x, quantity: 0 }));
    }
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  updateOption(optionIdx: number, value: boolean) {
    const { quantity } = this.addOns[optionIdx];
    if (quantity === 0) {
      this.addOns[optionIdx].quantity = this.addOns[optionIdx].minQuantity;
    } else {
      this.addOns[optionIdx].quantity = 0;
    }
    this.calculateOptionalAddOnsTotal();
  }

  incrementOptionQuantity(optionIdx: number) {
    this.addOns[optionIdx].quantity += 1;
    this.calculateOptionalAddOnsTotal();
  }

  decrementOptionQuantity(optionIdx: number) {
    const { quantity = 1 } = this.addOns[optionIdx];
    if (quantity > 1) {
      this.addOns[optionIdx].quantity -= 1;
      this.calculateOptionalAddOnsTotal();
    }
  }

  hasOptionalAddOns() {
    return this.addOns.some((x) => x.quantity > 0);
  }

  calculateOptionalAddOnsTotal() {
    this.optionalAddOnsTotal = getAddOnsTotal(this.addOns, 'optional', true);
  }

  trackByFn(index, item) {
    return index;
  }

  manageAdjustmentPaymentFromCharter(charter) {
    return new Observable((observer) => {
      this.paymentDAO
        .getPaymentsByCharterId(charter.id)
        .subscribe((payments) => {
          const [fullAndAddOnPaymentTotal, paidPaymentTotal] = payments.reduce(
            ([full, paid], curr) => {
              const amount = parseFloat(curr.amount);
              if (
                [PaymentType.FULL, PaymentType.ADJUSTMENT].includes(curr.type)
              ) {
                full += amount;
                if (curr.paid) {
                  paid += amount;
                }
              }
              if (curr.type === PaymentType.REFUND) {
                full -= amount;
                paid -= amount;
              }
              return [full, paid];
            },
            [0, 0],
          );

          const requiredAddOns = this.charter.pricingMetaData.addOns.filter(
            (x) => x?.required,
          );
          this.charter.pricingMetaData.addOns = [
            ...requiredAddOns,
            ...this.addOns,
          ];
          const { discountsTotal } = this.calculateDiscounts();
          const promoAmount = getPromoAmountFromCharter(charter);
          charter.discountTotal = discountsTotal;
          const newBoatFullAndAddOnPrice = parseFloat(
            getRoundedFullPaymentAmount(charter, promoAmount),
          );

          const unpaidAdjustmentPayment = payments.find(
            (x) => x.type === PaymentType.ADJUSTMENT && !x.paid,
          );
          const adjustmentDiff = newBoatFullAndAddOnPrice - paidPaymentTotal;
          const adjustmentPrice =
            adjustmentDiff > 0 ? roundToNearestCent(adjustmentDiff) : 0;

          if (
            unpaidAdjustmentPayment &&
            fullAndAddOnPaymentTotal !== newBoatFullAndAddOnPrice
          ) {
            if (adjustmentPrice === 0) {
              // if adjustment payment will be set to zero, delete it instead
              this.paymentDAO
                .deleteAdjustmentPayment(unpaidAdjustmentPayment.id)
                .subscribe((success) => {
                  observer.next(!!success);
                  observer.complete();
                });
            } else {
              // update amount of adjustment payment to include entire charter adjustment
              const paymUpdateSub = this.paymentDAO
                .updateAdjustmentPayment(unpaidAdjustmentPayment.id, {
                  amount: `${adjustmentPrice}`,
                })
                .subscribe((success) => {
                  observer.next(!!success);
                  observer.complete();
                });
              this.subs.push(paymUpdateSub);
            }
          } else if (fullAndAddOnPaymentTotal < newBoatFullAndAddOnPrice) {
            // create payment intent of dif
            const adjustmentPaymentData: Payment = {
              type: PaymentType.ADJUSTMENT,
              name: 'Charter edit - Price increase',
              paid: false,
              notes: 'charter price adjustment',
              amount: `${adjustmentPrice}`,
              charterId: charter.id,
              transactionCreatedAt: moment().utc().format('YYYY-MM-DD HH:mm'),
              processor: PaymentProcessor.STRIPE,
            };
            this.paymentDAO
              .createAdjustmentPayment(adjustmentPaymentData)
              .subscribe((payment) => {
                observer.next(true);
                observer.complete();
              });
          } else {
            observer.next(true);
            observer.complete();
          }
        });
    });
  }

  calculateDiscounts(): { discounts: any; discountsTotal: number } {
    let discountsTotal = 0;
    let discounts = [];
    (this.charter?.discounts || []).forEach((el) => {
      if (el.name !== '') {
        el.amount = this.numFormatter.unMaskPrice(el.amount);
        discounts.push(el);
        discountsTotal += this.numFormatter.unMaskPrice(el.amount);
      }
    });
    return { discounts, discountsTotal };
  }
}
